import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import {
  Newspaper,
  PaperPlaneTilt,
  Envelope,
  Eye,
  ArrowClockwise,
  Warning,
  CheckCircle,
  XCircle,
  DownloadSimple,
  Users,
  UserMinus,
  TextB,
  TextItalic,
  TextUnderline,
  TextHOne,
  TextHTwo,
  ListBullets,
  ListNumbers,
  Link as LinkIcon,
  Quotes,
} from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";

// ---------------- Rich Text Editor ----------------
const EditorToolbarButton = ({ active, onClick, disabled, children, label, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    data-testid={testId}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-md text-sm transition-colors ${
      active
        ? "bg-primary text-white"
        : "text-ink-700 hover:bg-ink-100 disabled:opacity-40 disabled:cursor-not-allowed"
    }`}
  >
    {children}
  </button>
);

const RichEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose max-w-none focus:outline-none min-h-[280px] px-5 py-4 text-ink-800",
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter URL (leave empty to remove link):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url, target: "_blank" })
      .run();
  };

  return (
    <div className="border border-ink-200 rounded-xl overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-ink-100 bg-softblue/30 px-2 py-1.5">
        <EditorToolbarButton
          testId="editor-h1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          label="Heading 1"
        >
          <TextHOne size={16} weight="duotone" />
        </EditorToolbarButton>
        <EditorToolbarButton
          testId="editor-h2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          label="Heading 2"
        >
          <TextHTwo size={16} weight="duotone" />
        </EditorToolbarButton>
        <span className="w-px h-5 bg-ink-200 mx-1" />
        <EditorToolbarButton
          testId="editor-bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
        >
          <TextB size={16} weight="bold" />
        </EditorToolbarButton>
        <EditorToolbarButton
          testId="editor-italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italic"
        >
          <TextItalic size={16} weight="duotone" />
        </EditorToolbarButton>
        <EditorToolbarButton
          testId="editor-underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="Underline"
        >
          <TextUnderline size={16} weight="duotone" />
        </EditorToolbarButton>
        <span className="w-px h-5 bg-ink-200 mx-1" />
        <EditorToolbarButton
          testId="editor-ul"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bullet list"
        >
          <ListBullets size={16} weight="duotone" />
        </EditorToolbarButton>
        <EditorToolbarButton
          testId="editor-ol"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Numbered list"
        >
          <ListNumbers size={16} weight="duotone" />
        </EditorToolbarButton>
        <EditorToolbarButton
          testId="editor-quote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="Quote"
        >
          <Quotes size={16} weight="duotone" />
        </EditorToolbarButton>
        <span className="w-px h-5 bg-ink-200 mx-1" />
        <EditorToolbarButton
          testId="editor-link"
          active={editor.isActive("link")}
          onClick={addLink}
          label="Add / remove link"
        >
          <LinkIcon size={16} weight="duotone" />
        </EditorToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

// ---------------- Preview ----------------
// Sanitize newsletter preview HTML to block script tags and event-handler injection
// while preserving all TipTap-emitted formatting (h1-h3, strong/em/u, ul/ol/blockquote, a).
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "u", "s",
    "h1", "h2", "h3", "h4",
    "ul", "ol", "li",
    "blockquote", "code", "pre",
    "a", "span",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ALLOW_DATA_ATTR: false,
};
const sanitizePreview = (html) => DOMPurify.sanitize(html || "", SANITIZE_CONFIG);

const NewsletterPreview = ({ subject, html }) => (
  <div className="border border-ink-100 rounded-xl bg-softblue/20 p-4 sm:p-5" data-testid="newsletter-preview">
    <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold mb-2">
      Preview
    </div>
    <div className="bg-white rounded-lg border border-ink-100 shadow-sm overflow-hidden">
      <div className="bg-softblue/40 border-b border-ink-100 px-4 py-3 text-xs text-ink-600">
        <span className="text-ink-500">Subject: </span>
        <span className="font-semibold text-ink-800">{subject || "(no subject)"}</span>
      </div>
      <div className="px-5 py-4">
        <div
          className="prose prose-sm max-w-none text-ink-800"
          dangerouslySetInnerHTML={{
            __html: sanitizePreview(html) || "<p class='text-ink-400'>Empty message</p>",
          }}
        />
        <div className="mt-6 pt-4 border-t border-ink-100 text-xs text-ink-500">
          You are receiving this because you subscribed to Begin Again Living Ministries.
          <br />
          <span className="underline text-primary/70">Unsubscribe with one click</span>
        </div>
      </div>
    </div>
  </div>
);

// ---------------- CSV Export ----------------
const buildSubscribersCSV = (subs) => {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[,\n"]/.test(s) ? `"${s}"` : s;
  };
  const rows = [
    ["Email", "Name", "Status", "Created At", "Unsubscribed At", "Source Page"],
    ...subs.map((s) => [
      s.email,
      s.name || "",
      s.unsubscribed ? "Unsubscribed" : "Active",
      s.created_at || "",
      s.unsubscribed_at || "",
      s.source_page || "",
    ]),
  ];
  return rows.map((r) => r.map(escape).join(",")).join("\n");
};

const downloadCSV = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// ---------------- Confirm Modal ----------------
const ConfirmSendModal = ({ open, recipientCount, subject, onCancel, onConfirm }) => {
  const [confirmText, setConfirmText] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setSending(false);
    }
  }, [open]);
  if (!open) return null;
  const canSend = confirmText === "SEND" && !sending;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 backdrop-blur-sm px-4"
      data-testid="newsletter-confirm-modal"
    >
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-ink-100 p-7">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-amber-100 text-amber-700 shrink-0">
            <Warning size={22} weight="duotone" />
          </span>
          <div>
            <h3 className="font-heading text-xl text-ink-900 font-medium">
              Send newsletter to {recipientCount} subscriber{recipientCount === 1 ? "" : "s"}?
            </h3>
            <p className="mt-2 text-sm text-ink-600 leading-relaxed">
              This will deliver &ldquo;<span className="font-semibold text-ink-800">{subject || "(no subject)"}</span>&rdquo; to
              every <strong>active</strong> subscriber. Unsubscribed addresses will be excluded automatically.
              This action cannot be undone.
            </p>
          </div>
        </div>
        <div className="mt-5">
          <label className="block text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-2">
            Type <span className="font-mono text-ink-900">SEND</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="SEND"
            data-testid="newsletter-confirm-input"
            className="w-full rounded-lg border border-ink-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono"
          />
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={sending}
            data-testid="newsletter-confirm-cancel"
            className="px-5 py-2.5 rounded-full font-semibold border border-ink-200 text-ink-700 hover:border-ink-400 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              setSending(true);
              try {
                await onConfirm();
              } finally {
                setSending(false);
              }
            }}
            disabled={!canSend}
            data-testid="newsletter-confirm-send"
            className="px-5 py-2.5 rounded-full font-semibold text-white bg-primary hover:bg-primary-dark disabled:bg-ink-300 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <PaperPlaneTilt size={16} weight="duotone" />
            {sending ? "Sending…" : `Send to ${recipientCount}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------- Main Newsletter Manager ----------------
const NewsletterManager = () => {
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("<p>Dear friend,</p><p></p>");
  const [subs, setSubs] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API_BASE}/admin/newsletter/subscribers`),
        axios.get(`${API_BASE}/admin/newsletter/sends`),
      ]);
      setSubs(s.data);
      setHistory(h.data || []);
    } catch (e) {
      toast.error("Couldn't load newsletter data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const activeCount = subs?.active_count ?? 0;
  const unsubCount = subs?.unsubscribed_count ?? 0;

  const canCompose = useMemo(() => subject.trim().length > 0 && html.replace(/<[^>]*>/g, "").trim().length > 0, [subject, html]);

  const handleSendTest = async () => {
    if (!canCompose) {
      toast.error("Add a subject and body before sending a test");
      return;
    }
    setSendingTest(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/newsletter/send-test`, {
        subject,
        html,
      });
      toast.success(`Test sent to ${res.data.sent_to}`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendBroadcast = async () => {
    try {
      const res = await axios.post(`${API_BASE}/admin/newsletter/send`, {
        subject,
        html,
        confirm: true,
        confirm_text: "SEND",
      });
      toast.success(
        `Sent to ${res.data.success_count}/${res.data.recipient_count} subscribers`
      );
      setModalOpen(false);
      setSubject("");
      setHtml("<p>Dear friend,</p><p></p>");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
      throw e;
    }
  };

  const handleExportCSV = () => {
    if (!subs?.subscribers?.length) {
      toast.error("Nothing to export");
      return;
    }
    const csv = buildSubscribersCSV(subs.subscribers);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCSV(`bal-newsletter-subscribers-${stamp}.csv`, csv);
    toast.success(`Exported ${subs.subscribers.length} subscribers`);
  };

  return (
    <div data-testid="newsletter-manager" className="space-y-5">
      {/* Header stats + export */}
      <div className="bg-white rounded-2xl border border-ink-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-5 flex flex-wrap items-center gap-4">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-softblue text-primary">
          <Newspaper size={20} weight="duotone" />
        </span>
        <div className="flex flex-wrap items-center gap-5 flex-1">
          <div data-testid="newsletter-active-count">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-semibold flex items-center gap-1">
              <Users size={12} weight="duotone" /> Active
            </div>
            <div className="font-heading text-2xl text-ink-900 font-medium">{activeCount}</div>
          </div>
          <div data-testid="newsletter-unsub-count">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-semibold flex items-center gap-1">
              <UserMinus size={12} weight="duotone" /> Unsubscribed
            </div>
            <div className="font-heading text-2xl text-ink-500 font-medium">{unsubCount}</div>
          </div>
        </div>
        <button
          onClick={fetchAll}
          data-testid="newsletter-refresh"
          className="inline-flex items-center gap-1.5 border border-ink-200 rounded-full px-3.5 py-2 text-sm font-semibold text-ink-700 hover:border-primary hover:text-primary transition-colors"
        >
          <ArrowClockwise size={14} weight="duotone" />
          Refresh
        </button>
        <button
          onClick={handleExportCSV}
          data-testid="newsletter-export-csv"
          className="inline-flex items-center gap-1.5 bg-primary text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-primary-dark transition-colors"
        >
          <DownloadSimple size={14} weight="duotone" />
          Export CSV
        </button>
      </div>

      {/* Compose */}
      <div className="bg-white rounded-2xl border border-ink-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-5 sm:p-6 space-y-5" data-testid="newsletter-compose">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-primary font-semibold mb-2">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. October Reflections from Begin Again Living Ministries"
            data-testid="newsletter-subject"
            className="w-full rounded-xl border border-ink-200 px-4 py-3 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-primary font-semibold mb-2">
            Message
          </label>
          <RichEditor value={html} onChange={setHtml} />
        </div>

        <NewsletterPreview subject={subject} html={html} />

        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-ink-100">
          <button
            onClick={handleSendTest}
            disabled={sendingTest || !canCompose}
            data-testid="newsletter-send-test"
            className="inline-flex items-center gap-2 border border-primary text-primary rounded-full px-5 py-2.5 font-semibold hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Envelope size={16} weight="duotone" />
            {sendingTest ? "Sending test…" : "Send test to me"}
          </button>
          <button
            onClick={() => setModalOpen(true)}
            disabled={!canCompose || activeCount === 0}
            data-testid="newsletter-send-open-modal"
            className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-2.5 font-semibold hover:bg-primary-dark transition-colors disabled:bg-ink-300 disabled:cursor-not-allowed"
          >
            <PaperPlaneTilt size={16} weight="duotone" />
            Send to {activeCount} subscriber{activeCount === 1 ? "" : "s"}
          </button>
        </div>
      </div>

      {/* Send history */}
      <div className="bg-white rounded-2xl border border-ink-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] overflow-hidden" data-testid="newsletter-history">
        <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.15em] text-ink-500 font-semibold">
            Send history
          </span>
          <span className="text-xs text-ink-500">
            {history.length} record{history.length === 1 ? "" : "s"}
          </span>
        </div>
        {loading ? (
          <div className="py-10 text-center text-sm text-ink-500">Loading…</div>
        ) : history.length === 0 ? (
          <div className="py-10 text-center text-sm text-ink-500">
            No newsletters sent yet.
          </div>
        ) : (
          <ul className="divide-y divide-ink-100 max-h-[400px] overflow-y-auto">
            {history.map((h) => (
              <li
                key={h.id}
                className="px-5 py-4 flex flex-wrap items-center gap-3"
                data-testid={`history-row-${h.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-ink-900 truncate">
                      {h.subject}
                    </span>
                    {h.is_test && (
                      <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                        Test
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-500 mt-1">
                    Sent {h.sent_at ? new Date(h.sent_at).toLocaleString() : "—"} · by {h.sent_by || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle size={14} weight="duotone" />
                    {h.success_count}
                  </span>
                  {h.failure_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-rose-700">
                      <XCircle size={14} weight="duotone" />
                      {h.failure_count}
                    </span>
                  )}
                  <span className="text-ink-500">/ {h.recipient_count}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmSendModal
        open={modalOpen}
        recipientCount={activeCount}
        subject={subject}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleSendBroadcast}
      />
    </div>
  );
};

export default NewsletterManager;
